import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faBell } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import type { Color } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { MY_SORARE_PURCHASES_AUCTIONS } from '@sorare/core/src/constants/__generated__/routes';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useCheckForMobilePushNotifications } from '@sorare/core/src/hooks/useCheckForMobilePushNotifications';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useLoggedCallback } from '@sorare/core/src/hooks/useLoggedCallback';

import { useAddReminder } from '#src/hooks/auctions/useAddReminder.ts';
import { useRemoveReminder } from '#src/hooks/auctions/useRemoveReminder.ts';

import { AddReminderDownloadApp } from './AddReminderDownloadApp/index.tsx';
import type { AuctionReminder_auction } from './__generated__/index.graphql.ts';

type Props = {
  auction: AuctionReminder_auction;
  withText?: boolean;
  small?: boolean;
  color: Color;
};
const StyledLink = styled(Link)`
  text-decoration: underline;
`;

const ToggleAuctionReminderButton = ({
  auction,
  withText,
  small,
  onEnabled,
  color: colorProps,
}: Props & { onEnabled: () => void }) => {
  const { isIosApp } = useIsMobileApp();
  const { myReminder } = auction;
  const [reminder, updateReminder] = useState(myReminder);
  const { showNotification } = useSnackNotificationContext();
  const { addReminder, loading: addReminderLoading } = useAddReminder();
  const { removeReminder, loading: removeReminderLoading } =
    useRemoveReminder();

  const reminderEnabled = !!reminder?.enabled;

  const toggleReminder = async () => {
    if (reminderEnabled) {
      const updatedReminder = await removeReminder({
        id: auction.id,
        reminder,
      });
      if (updatedReminder) {
        updateReminder(updatedReminder);
        showNotification('reminderSuccessfullyRemovedFromAuction');
      }
    } else {
      const updatedReminder = await addReminder({ auction });
      if (updatedReminder) {
        updateReminder(updatedReminder);
        showNotification('reminderSuccessfullyAddedOnAuction', {
          myAuctionsLink: (chunks: ReactNode): ReactNode =>
            isIosApp ? (
              chunks
            ) : (
              <StyledLink to={MY_SORARE_PURCHASES_AUCTIONS}>
                {chunks}
              </StyledLink>
            ),
        });
        onEnabled();
      }
    }
  };
  const updating = addReminderLoading || removeReminderLoading;

  const onClick = useLoggedCallback(() => {
    if (updating) return;
    toggleReminder();
  });

  const color = reminderEnabled ? 'primary' : colorProps;

  if (withText && !reminderEnabled) {
    return (
      <Button
        size={small ? 'small' : 'medium'}
        fullWidth
        onClick={onClick}
        color={color}
      >
        <FormattedMessage defaultMessage="Notify me" />
        <FontAwesomeIcon icon={faBell} />
      </Button>
    );
  }
  return (
    <IconButton
      size={small ? 'small' : 'medium'}
      onClick={onClick}
      icon={faBell}
      color={color}
    />
  );
};

export const AuctionReminder = ({ auction, withText, small, color }: Props) => {
  const { isMobileApp } = useIsMobileApp();
  const { open } = auction;
  const [dialogOpen, setDialogOpen] = useState(false);
  const { update: updateLifecycle, lifecycle } = useLifecycle();
  const checkForMobilePushNotifications = useCheckForMobilePushNotifications();

  if (!open) {
    return null;
  }

  const doNotShowDownloadApp =
    lifecycle?.doNotShowAddAuctionReminderDownloadApp;
  const onEnabledReminder = () => {
    checkForMobilePushNotifications();
    if (!isMobileApp && !doNotShowDownloadApp) {
      setDialogOpen(true);
    }
  };

  const onCloseDialog = (doNotShowAgain: boolean) => {
    setDialogOpen(false);

    if (doNotShowAgain) {
      updateLifecycle(LIFECYCLE.doNotShowAddAuctionReminderDownloadApp, true);
    }
  };

  return (
    <>
      <AddReminderDownloadApp open={dialogOpen} onClose={onCloseDialog} />
      <ToggleAuctionReminderButton
        auction={auction}
        small={small}
        withText={withText}
        onEnabled={onEnabledReminder}
        color={color}
      />
    </>
  );
};

AuctionReminder.fragments = {
  auction: gql`
    fragment AuctionReminder_auction on TokenAuction {
      id
      open
      myReminder {
        id
        enabled
      }
    }
  ` as TypedDocumentNode<AuctionReminder_auction>,
};
